import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  AlertOctagon,
  AlertTriangle,
  BookOpen,
  Building2,
  ChevronRight,
  Cloud,
  CloudSun,
  Equal,
  Home,
  ListChecks,
  Settings,
  Sun,
  Thermometer,
} from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { useCountUp } from "@/lib/hooks/use-count-up";
import { cn } from "@/lib/utils";
import { HeatBadge } from "@/components/ui/heat-badge";
import { WarningCard } from "@/components/ui/warning-card";
import { ActionButton } from "@/components/ui/action-button";
import { QuickAddCard } from "@/components/dashboard/quick-add-card";
import { PageHeader } from "@/components/layout/page-header";
import { ElternPushTrigger } from "@/features/push/eltern-push-modal";
import { AutoLevelToast } from "./auto-level-toast";
import { useAutoToastOnLevelChange } from "./auto-toast-on-level-change";
import { TrendSparklineCard } from "./dashboard-trend-card";
import { ForecastTodayCard } from "./forecast-today-card";
import {
  computeHourlyDueRooms,
  type HourlyDueResult,
} from "./hourly-due-rooms";
import { HourlyMeasureCard } from "./hourly-measure-card";
import { useHourlyMeasureNudge } from "./use-hourly-measure-nudge";
import { STUFEN_INFO, type ConfirmedLevel } from "@/lib/stufen-labels";

/**
 * Dashboard-route — hauptscreen der app.
 *
 * Daten kommen ALLE aus einem call: GET /v1/dashboard/status.
 * Server ist single-source-of-truth für die stufen-klassifikation —
 * die PWA rendert nur. Bei netzwerk-fail greift workbox-cache aus
 * dem letzten erfolgreichen call (NetworkFirst mit 5s timeout +
 * 5min ttl, siehe vite.config.ts).
 *
 * Phase 13 — calm+premium dashboard-redesign: PageHeader mit org-context,
 * hero mit 2-col-grid (gradient-stufen-display + 3-col stat-werte),
 * counter-up + stagger-fade-in + pulse-ring (stufe 4). CSS-only animationen,
 * keine motion-lib. Carve-out-doku in DESIGN-SYSTEM.md §8.1.
 */

interface DashboardStatus {
  generated_at: string;
  org_id: string;
  org_name: string;
  org_plz?: string;
  org_city?: string;
  current_level: "normal" | "soll" | "muss" | "kritisch";
  current_level_source: "indoor" | "outdoor" | "tie";
  weather?: {
    temperature_c: number;
    humidity: number;
    uv_index: number;
    heat_level: "normal" | "soll" | "muss" | "kritisch";
    observed_at: string;
    source: string;
    fallback: boolean;
    dwd_warning_code?: number;
    dwd_warning_text?: string;
  };
  // Contract-flat-felder (Rolle D, backend-fix parallel). Der server kann
  // outdoor-wetter entweder verschachtelt unter `weather` ODER flach
  // top-level liefern. resolveCurrentWeather() liest beide formen, damit
  // die anzeige unabhängig von der finalen backend-form funktioniert.
  // sentinel: uv_index === -1 = "unknown" (siehe weather/types.go), und
  // dwd_warning_code === 0 = "keine warnung" (schema 0001_init).
  outdoor_temp_c?: number | null;
  uv_index?: number | null;
  dwd_warning_code?: number | null;
  dwd_warning_text?: string | null;
  indoor_max_c?: number;
  recommended_measures: Array<{
    id: string;
    code: string;
    heat_level: string;
    kind: string;
    title: string;
    description: string;
    priority: string;
    legal_reference?: string;
    sort_order: number;
  }>;
  rooms: Array<{
    room_id: string;
    name: string;
    etage?: string;
    kapazitaet?: number;
    sort_order: number;
    last_entry?: {
      entry_id: string;
      event_at: string;
      indoor_temp_c: number;
      outdoor_temp_c?: number;
      confirmed_level: "normal" | "soll" | "muss" | "kritisch";
      is_provisional: boolean;
    };
  }>;
  entries_last_7d: number;
}

/**
 * Bridge: API-Enum-String → DB-Wert 0..3 (ConfirmedLevel).
 * stufen-labels.ts ist single-source für Labels + Tokens — hier nur das Mapping.
 */
const LEVEL_TO_CONFIRMED: Record<DashboardStatus["current_level"], ConfirmedLevel> = {
  normal: 0,
  soll: 1,
  muss: 2,
  kritisch: 3,
};

function buildHeaderActions(currentLevel: DashboardStatus["current_level"]): React.ReactElement {
  return (
    <div
      role="toolbar"
      aria-label="Dashboard-Aktionen"
      className={cn(
        "flex items-center gap-2",
        // Mobile: horizontal scroll mit snap, scrollbar visuell weg (scroll-area
        // funktioniert weiter). Negative margins damit scroll bis screen-edge
        // geht — bessere thumb-reach.
        "-mx-4 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        "snap-x snap-mandatory",
        // md+: kein scroll, kein neg-margin, flex-wrap als safety-net.
        "md:m-0 md:flex-wrap md:overflow-visible md:p-0",
      )}
    >
      <ActionButton to="/entries" icon={ListChecks} label="Einträge" disabled className="snap-start" />
      <ActionButton to="/wiki" icon={BookOpen} label="Wiki" className="snap-start" />
      <ActionButton to="/raeume" icon={Building2} label="Räume" className="snap-start" />
      <ElternPushTrigger currentLevel={currentLevel} className="snap-start" />
      {/* #7 fix: "Export" button entfernt — non-traeger-user haben heute keinen
          dedicated export-flow. Export-funktionalität liegt für träger unter
          /traeger/export, für einzel-orgs ist es Y2-feature. Misleading "Export"-
          button der zu /einstellungen routete wurde entfernt. */}
      <ActionButton to="/einstellungen" icon={Settings} label="Einstellungen" className="snap-start" />
    </div>
  );
}

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export function DashboardRoute() {
  const status = useQuery({
    queryKey: ["dashboard", "status"],
    queryFn: () => apiFetch<DashboardStatus>("/v1/dashboard/status"),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Hook wird BEFORE early-returns aufgerufen — bei nullable-args (no-op via
  // !orgId in hook) garantiert hook-call-count konstant. Schützt vor rules-of-
  // hooks-violation bei späteren refactors.
  const currentLevelForToast = status.data ? LEVEL_TO_CONFIRMED[status.data.current_level] : 0;
  useAutoToastOnLevelChange(currentLevelForToast, status.data?.org_id);

  // B7 — stündliche Mess-Fälligkeit bei Kritisch (FE-only aus last_entry
  // abgeleitet). Einmal pro render berechnet, für Karte + Stunden-Nudge
  // wiederverwendet. Vor early-returns wegen rules-of-hooks (nudge).
  const due: HourlyDueResult = status.data
    ? computeHourlyDueRooms(status.data.rooms, new Date())
    : { openThisHour: [], doneThisHour: [], anyKritischToday: false };
  useHourlyMeasureNudge(due.openThisHour.length, status.data?.org_id);

  if (status.isPending) {
    return <DashboardSkeleton />;
  }
  if (status.isError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Dashboard" />
        <section className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-heat-4-soft)] p-6 shadow-[var(--shadow-md)]">
          <h2 className="text-xl font-semibold">Daten nicht verfügbar</h2>
          <p className="mt-2 text-sm">
            Aktuelle Stufe kann nicht geladen werden. Bei aktivem Offline-Modus zeigen wir das
            letzte gespeicherte Bild — bitte prüfen Sie Datum oben rechts.
          </p>
        </section>
      </div>
    );
  }
  const data = status.data;
  const currentLevel = LEVEL_TO_CONFIRMED[data.current_level];

  const now = Date.now();
  const staleRooms = data.rooms.filter((r) => {
    if (!r.last_entry) return false;
    const eventTs = Date.parse(r.last_entry.event_at);
    return Number.isFinite(eventTs) && now - eventTs > STALE_THRESHOLD_MS;
  });
  const provisionalRooms = data.rooms.filter((r) => r.last_entry?.is_provisional);
  const hasWarnings = !data.org_plz || staleRooms.length > 0 || provisionalRooms.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        orgName={data.org_name}
        orgCity={data.org_city}
        orgPlz={data.org_plz}
        actions={buildHeaderActions(data.current_level)}
      />
      {hasWarnings && (
        <div className="space-y-3">
          {!data.org_plz && (
            <WarningCard
              severity="info"
              title="PLZ fehlt — Außenwetter wird nicht geladen"
              description="Ergänzen Sie die PLZ Ihrer Einrichtung, damit DWD-Daten für die Stufen-Berechnung herangezogen werden."
              action={{ label: "Zur Einstellung", href: "/einstellungen" }}
            />
          )}
          {staleRooms.length > 0 && (
            <WarningCard
              severity="warning"
              title={`${staleRooms.length} ${staleRooms.length === 1 ? "Raum" : "Räume"} ohne Messung seit über 24 Stunden`}
              description={
                staleRooms.length <= 3
                  ? `Betroffen: ${staleRooms.map((r) => r.name).join(", ")}.`
                  : `Betroffen u.a.: ${staleRooms
                      .slice(0, 3)
                      .map((r) => r.name)
                      .join(", ")} und ${staleRooms.length - 3} weitere.`
              }
              action={{ label: "Räume prüfen", href: "/raeume" }}
            />
          )}
          {provisionalRooms.length > 0 && (
            <WarningCard
              severity="info"
              title={`${provisionalRooms.length} ${provisionalRooms.length === 1 ? "provisorischer Eintrag" : "provisorische Einträge"} offen`}
              description={
                provisionalRooms.length <= 3
                  ? `Betroffen: ${provisionalRooms.map((r) => r.name).join(", ")}. Bitte am Tagesende bestätigen.`
                  : `Betroffen u.a.: ${provisionalRooms
                      .slice(0, 3)
                      .map((r) => r.name)
                      .join(", ")} und ${provisionalRooms.length - 3} weitere. Bitte am Tagesende bestätigen.`
              }
              action={{ label: "Räume prüfen", href: "/raeume" }}
            />
          )}
        </div>
      )}
      <CurrentStatusHero key={data.org_id} data={data} currentLevel={currentLevel} />
      <HourlyMeasureCard due={due} />
      <ForecastTodayCard orgHasPlz={!!data.org_plz} />
      <TrendSparklineCard />
      <QuickAddCard />
      <RecommendedMeasuresCard measures={data.recommended_measures} />
      <RoomsOverviewGrid rooms={data.rooms} />
      <DashboardMeta data={data} />
      <AutoLevelToast />
    </div>
  );
}

interface DriverInfo {
  text: string;
  roomId: string | null;
}

function buildDriverInfo(data: DashboardStatus): DriverInfo {
  if (data.current_level_source === "outdoor") {
    // Außentemp aus flat (outdoor_temp_c) ODER weather-sub — robust gegen
    // beide contract-formen (parallel von Rolle D gefixt).
    const flatTemp =
      typeof data.outdoor_temp_c === "number" && Number.isFinite(data.outdoor_temp_c)
        ? data.outdoor_temp_c
        : null;
    const nestedTemp =
      data.weather && Number.isFinite(data.weather.temperature_c)
        ? data.weather.temperature_c
        : null;
    const t = flatTemp ?? nestedTemp;
    if (t !== null) {
      return { text: `Außenwetter ${formatTemp(t)} (DWD)`, roomId: null };
    }
    return { text: "Außenwetter (DWD)", roomId: null };
  }
  if (data.current_level_source === "indoor") {
    // Primär: räume mit confirmed_level == aktueller gesamtstufe (echte treiber),
    // tiebreak nach raw-temp desc. Fallback (z.B. server-classifier weicht ab):
    // höchste temp-overall.
    const sortByTempDesc = (
      a: DashboardStatus["rooms"][number],
      b: DashboardStatus["rooms"][number],
    ) =>
      (b.last_entry?.indoor_temp_c ?? -Infinity) -
      (a.last_entry?.indoor_temp_c ?? -Infinity);
    const matching = data.rooms
      .filter((r) => r.last_entry?.confirmed_level === data.current_level)
      .sort(sortByTempDesc);
    const driverRoom =
      matching[0] ??
      [...data.rooms].filter((r) => r.last_entry).sort(sortByTempDesc)[0];
    if (driverRoom?.last_entry) {
      return {
        text: `${driverRoom.name} mit ${formatTemp(driverRoom.last_entry.indoor_temp_c)}`,
        roomId: driverRoom.room_id,
      };
    }
  }
  if (data.current_level_source === "tie") {
    return { text: "Innen + Außen gleichauf", roomId: null };
  }
  return { text: "Keine Datenquelle", roomId: null };
}

/**
 * resolveCurrentWeather — normalisiert die wetter-felder der status-response
 * über beide möglichen contract-formen (Rolle D backend-fix, #4):
 *   1. flat top-level: data.outdoor_temp_c / data.uv_index / data.dwd_warning_*
 *   2. verschachtelt:   data.weather.temperature_c / .uv_index / .heat_level
 *
 * Flat-felder haben vorrang (= der explizit erwartete contract), das
 * weather-sub-objekt ist fallback. Sentinel-werte (uv === -1, temp/uv null)
 * → null, damit StatItem graceful "—" rendert.
 */
interface ResolvedWeather {
  outdoorTempC: number | null;
  uvIndex: number | null;
  /** klartext-status fürs "Wetter"-feld (DWD-warntext oder heat-level-label) */
  conditionText: string | null;
  dwdWarning: { code: number; text: string } | null;
  /** open-meteo-fallback aktiv (CC-BY-hinweis) */
  isFallback: boolean;
}

const HEAT_LEVEL_CONDITION: Record<DashboardStatus["current_level"], string> = {
  normal: "Normal",
  soll: "Soll-Stufe",
  muss: "Muss-Stufe",
  kritisch: "Hitzewarnung",
};

function isUsableNumber(v: number | null | undefined): v is number {
  // -1 ist server-sentinel für "unknown" (uv_index), NaN/undefined/null raus.
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}

function resolveCurrentWeather(data: DashboardStatus): ResolvedWeather {
  // Außentemp: flat zuerst, dann weather-sub. Negative temp ist physikalisch
  // gültig (winter), darum hier KEINE >= 0-prüfung — nur finite.
  const flatTemp =
    typeof data.outdoor_temp_c === "number" && Number.isFinite(data.outdoor_temp_c)
      ? data.outdoor_temp_c
      : null;
  const nestedTemp =
    data.weather && Number.isFinite(data.weather.temperature_c)
      ? data.weather.temperature_c
      : null;
  const outdoorTempC = flatTemp ?? nestedTemp;

  // UV-index: -1 = unknown (sentinel) → null.
  const flatUv = isUsableNumber(data.uv_index) ? data.uv_index : null;
  const nestedUv =
    data.weather && isUsableNumber(data.weather.uv_index) ? data.weather.uv_index : null;
  const uvIndex = flatUv ?? nestedUv;

  // DWD-warnung: code 0 / leerer text = keine warnung.
  // Liest flat top-level ODER nested unter data.weather (analog temp/uv-fallback).
  const code = data.dwd_warning_code ?? data.weather?.dwd_warning_code ?? 0;
  const text = (data.dwd_warning_text ?? data.weather?.dwd_warning_text ?? "").trim();
  const dwdWarning = code > 0 || text !== "" ? { code, text } : null;

  // Condition-text fürs kompakt-feld: DWD-warntext hat vorrang, sonst
  // outdoor-heat-level-label (weather-sub), sonst gesamtstufe-label.
  let conditionText: string | null = null;
  if (text !== "") {
    conditionText = text;
  } else if (data.weather) {
    conditionText = HEAT_LEVEL_CONDITION[data.weather.heat_level];
  } else if (outdoorTempC !== null) {
    conditionText = HEAT_LEVEL_CONDITION[data.current_level];
  }

  return {
    outdoorTempC,
    uvIndex,
    conditionText,
    dwdWarning,
    isFallback: data.weather?.fallback ?? false,
  };
}

// UV-index → WHO-risikostufe (für das aria-label, color-blind-safe klartext).
function uvRiskLabel(uv: number): string {
  if (uv < 3) return "niedrig";
  if (uv < 6) return "mäßig";
  if (uv < 8) return "hoch";
  if (uv < 11) return "sehr hoch";
  return "extrem";
}

const uvFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function CurrentStatusHero({
  data,
  currentLevel,
}: {
  data: DashboardStatus;
  currentLevel: ConfirmedLevel;
}) {
  const animatedLevel = useCountUp({
    target: currentLevel,
    duration: 700,
    resetKey: data.org_id,
  });
  const driver = buildDriverInfo(data);
  // B3: Labels aus single-source stufen-labels.ts
  const stufeInfo = STUFEN_INFO[currentLevel];
  // heat-N-Token-Index für gradient-from/to: DB-Level 0→heat-1, 1→heat-2 usw.
  const heatTokenIdx = (currentLevel + 1) as 1 | 2 | 3 | 4;
  const wx = resolveCurrentWeather(data);

  return (
    <section
      aria-labelledby="hero-stufe-heute"
      className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:gap-6"
    >
      <article
        className={cn(
          "relative overflow-hidden rounded-2xl border border-[color:var(--color-border)]",
          "p-6 md:p-8",
          "shadow-[var(--shadow-md)]",
          currentLevel === 3 && "anim-pulse-ring",
        )}
        style={{
          backgroundImage: `linear-gradient(135deg, var(--color-heat-${heatTokenIdx}-gradient-from) 0%, var(--color-heat-${heatTokenIdx}-gradient-to) 100%)`,
        }}
      >
        <p className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-primary)]">
          Stufe heute
        </p>

        {/* B3: zeige Name + DB-Zahl (0-3), kein "/ 4" mehr */}
        <div className="mt-3 flex items-baseline gap-3">
          <span
            id="hero-stufe-heute"
            key={`stufe-${currentLevel}`}
            className={cn(
              "block",
              "text-[length:var(--text-display-lg)] md:text-[length:var(--text-display-xl)]",
              "font-bold leading-none tracking-[var(--letter-spacing-tight)]",
              "text-[color:var(--color-text-primary)]",
              "[font-variant-numeric:tabular-nums]",
              "anim-value-flip",
            )}
          >
            {animatedLevel}
          </span>
          <span className="text-xl font-semibold leading-none text-[color:var(--color-text-primary)] opacity-70">
            {stufeInfo.short}
          </span>
        </div>

        {stufeInfo.zusatz && (
          <p className="mt-1 text-sm font-medium text-[color:var(--color-text-primary)] opacity-80">
            {stufeInfo.zusatz}
          </p>
        )}

        <div className="mt-4">
          <SourceChip source={data.current_level_source} />
        </div>

        <DriverLink driver={driver} />
      </article>

      <article
        aria-labelledby="hero-werte-heading"
        className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-6 shadow-[var(--shadow-md)] md:p-8"
      >
        <h2
          id="hero-werte-heading"
          className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]"
        >
          Aktuelle Werte
        </h2>

        <dl className="mt-4 grid grid-cols-3 gap-4">
          <StatItem
            label="Außentemp."
            value={
              // Resolver liest flat (outdoor_temp_c) ODER weather.temperature_c.
              // Letzter fallback: indoor_max_c, wenn gar kein außenwert da ist.
              wx.outdoorTempC !== null
                ? formatTempRaw(wx.outdoorTempC)
                : data.indoor_max_c !== undefined
                  ? formatTempRaw(data.indoor_max_c)
                  : null
            }
            unit="°C"
            icon={Thermometer}
            staggerDelay={0}
            valueAriaLabel={
              wx.outdoorTempC === null && data.indoor_max_c !== undefined
                ? `Innen-Maximum ${formatTemp(data.indoor_max_c)} (kein Außenwert verfügbar)`
                : undefined
            }
          />
          <StatItem
            label="UV-Index"
            value={wx.uvIndex !== null ? uvFormatter.format(wx.uvIndex) : null}
            icon={Sun}
            staggerDelay={50}
            valueAriaLabel={
              wx.uvIndex !== null
                ? `UV-Index ${uvFormatter.format(wx.uvIndex)}, Risiko ${uvRiskLabel(wx.uvIndex)}`
                : undefined
            }
          />
          <StatItem
            label="Wetter"
            value={wx.conditionText}
            icon={Cloud}
            staggerDelay={100}
            isWide
          />
        </dl>

        {wx.dwdWarning && (
          // DWD-amtliche-warnung — color-blind-safe via icon + border-l-4 +
          // klartext (nicht nur farbe). Schwellen-mapping der DWD-warnstufen
          // (code 1xx hitze/UV-info, 2xx markante wärmebelastung, 3xx+ extrem)
          // → warning/critical. Code 0 / leerer text rendert diese zeile nicht.
          <div className="mt-4">
            <DwdWarningRow code={wx.dwdWarning.code} text={wx.dwdWarning.text} />
          </div>
        )}

        {wx.isFallback && (
          <p className="mt-4 text-xs text-[color:var(--color-text-secondary)]">
            Wetterdaten via Open-Meteo (EU-DSGVO, CC-BY 4.0).
            DWD-Direktanbindung ab Year-2.
          </p>
        )}
      </article>
    </section>
  );
}

function SourceChip({ source }: { source: DashboardStatus["current_level_source"] }) {
  const meta: { Icon: typeof Home; label: string } = (() => {
    switch (source) {
      case "indoor":
        return { Icon: Home, label: "Raum-Messwert" };
      case "outdoor":
        return { Icon: CloudSun, label: "Außenwetter (DWD)" };
      case "tie":
        return { Icon: Equal, label: "Innen + Außen gleichauf" };
    }
  })();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-black/5 px-2.5 py-1 text-xs font-medium text-[color:var(--color-text-primary)]">
      <meta.Icon aria-hidden="true" strokeWidth={2.25} className="size-3.5" />
      {meta.label}
    </span>
  );
}

function DriverLink({ driver }: { driver: DriverInfo }) {
  const content = (
    <>
      <span className="font-medium">Treiber:</span> {driver.text}
      {driver.roomId && (
        <ChevronRight aria-hidden="true" strokeWidth={2.25} className="size-3.5" />
      )}
    </>
  );
  const baseClasses = cn(
    "mt-3 inline-flex items-center gap-1.5",
    "-mx-2 min-h-[44px] rounded-md px-2 py-2.5",
    "text-sm text-[color:var(--color-text-primary)]",
    "transition-colors duration-150 ease-out",
  );
  if (driver.roomId) {
    return (
      <Link
        to={`/raeume/${driver.roomId}`}
        className={cn(
          baseClasses,
          "hover:bg-black/5 hover:text-[color:var(--color-text-primary)] hover:underline underline-offset-4",
        )}
      >
        {content}
      </Link>
    );
  }
  return <p className={baseClasses}>{content}</p>;
}

interface StatItemProps {
  label: string;
  value: string | null;
  unit?: string;
  icon: typeof Home;
  staggerDelay: number;
  isWide?: boolean;
  /** optionaler screenreader-klartext (z.B. UV-risiko), ersetzt die <dd>-vorlesung */
  valueAriaLabel?: string;
}

function StatItem({
  label,
  value,
  unit,
  icon: Icon,
  staggerDelay,
  isWide,
  valueAriaLabel,
}: StatItemProps) {
  const hasValue = value !== null;
  return (
    <div
      className="anim-stagger-in"
      style={{ animationDelay: `${staggerDelay}ms` }}
    >
      <Icon
        aria-hidden="true"
        strokeWidth={2}
        className="size-4 text-[color:var(--color-text-secondary)]"
      />
      <dt className="mt-2 text-xs font-medium text-[color:var(--color-text-secondary)]">
        {label}
      </dt>
      <dd
        className={cn(
          "mt-1 leading-tight",
          isWide
            ? "text-sm font-semibold"
            : "text-2xl font-semibold [font-variant-numeric:tabular-nums]",
          "text-[color:var(--color-text-primary)]",
        )}
        aria-label={
          valueAriaLabel ?? (!hasValue ? `${label}: keine Daten verfügbar` : undefined)
        }
      >
        <span aria-hidden={valueAriaLabel ? true : undefined}>
          {value ?? "—"}
          {unit && hasValue && (
            <span className="ml-0.5 text-base font-normal text-[color:var(--color-text-secondary)]">
              {unit}
            </span>
          )}
        </span>
      </dd>
    </div>
  );
}

/**
 * DwdWarningRow — abgeleitete Hitze-Vorwarnung in der "Aktuelle Werte"-card (#4).
 *
 * NICHT amtlich: codes 246/247/248 werden aus der Außentemperatur abgeleitet
 * (ClassifyOutdoorOnly), nicht aus dem echten DWD-CAP-Feed (unimplementiert).
 * Der anzuzeigende text kommt direkt vom Backend und enthält den Hinweis
 * "(abgeleitet aus Außentemp. — keine amtliche DWD-Warnung)".
 *
 * Severity-Mapping (unverändert):
 *   246        → erhöhte Belastung (orange, AlertTriangle, role=status)
 *   247 | 248  → hohe/extreme Belastung (rot, AlertOctagon, role=alert)
 *   ≥300       → fallback für andere extreme Codes (rot, AlertOctagon)
 *   sonstige   → Info/Vorwarnung (gelb, AlertTriangle, role=status)
 *
 * code=0 oder kein dwdWarning → diese Komponente wird gar nicht gerendert.
 * Color-blind-safe: icon + border-l-4 + klartext, nicht nur farbe.
 */
function DwdWarningRow({ code, text }: { code: number; text: string }) {
  const isExtreme = code === 247 || code === 248 || code >= 300;
  const isMarkant = code === 246;
  const Icon = isExtreme ? AlertOctagon : AlertTriangle;

  type Tone = { bg: string; border: string; icon: string };
  const tone: Tone = isExtreme
    ? {
        bg: "bg-[color:var(--color-heat-4-soft)]",
        border: "border-l-[color:var(--color-danger)]",
        icon: "text-[color:var(--color-danger)]",
      }
    : isMarkant
      ? {
          bg: "bg-[color:var(--color-heat-3-soft)]",
          border: "border-l-[color:var(--color-heat-3)]",
          icon: "text-[color:var(--color-heat-3)]",
        }
      : {
          bg: "bg-[color:var(--color-heat-2-soft)]",
          border: "border-l-[color:var(--color-warning)]",
          icon: "text-[color:var(--color-warning)]",
        };

  const label =
    text !== "" ? text : `Hitze-Vorwarnung (abgeleitet, Code ${code})`;
  return (
    <div
      role={isExtreme ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2 rounded-md border border-l-4 border-[color:var(--color-border)] p-2.5",
        tone.bg,
        tone.border,
      )}
    >
      <Icon
        aria-hidden="true"
        strokeWidth={2}
        className={cn("mt-0.5 size-4 shrink-0", tone.icon)}
      />
      <p className="text-xs font-medium leading-snug text-[color:var(--color-text-primary)]">
        <span className="sr-only">Hitze-Vorwarnung (abgeleitet): </span>
        {label}
      </p>
    </div>
  );
}

const tempFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatTempRaw(value: number): string {
  return tempFormatter.format(value);
}

function formatTemp(value: number): string {
  return `${tempFormatter.format(value)} °C`;
}

function RecommendedMeasuresCard({
  measures,
}: {
  measures: DashboardStatus["recommended_measures"];
}) {
  if (measures.length === 0) {
    return (
      <article className="rounded-lg border border-[color:var(--color-border)] bg-white p-6">
        <h2 className="text-xl font-semibold">Empfohlene Maßnahmen</h2>
        <p className="mt-2 text-sm text-neutral-600">
          Aktuelle Stufe: keine Pflicht-Maßnahmen. Routine-Hygiene (Lüften, Trinken) bleibt.
        </p>
      </article>
    );
  }
  const top = measures.slice(0, 5);
  return (
    <article className="rounded-lg border border-[color:var(--color-border)] bg-white p-6">
      <h2 className="text-xl font-semibold">Empfohlene Maßnahmen</h2>
      <ul className="mt-3 space-y-2">
        {top.map((m) => (
          <li
            key={m.id}
            className="flex items-start gap-3 rounded-md border border-[color:var(--color-border)] p-3"
          >
            <span
              className="mt-1 inline-block size-2 shrink-0 rounded-full bg-[color:var(--color-brand-primary)]"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-medium">{m.title}</p>
              <p className="mt-1 text-xs text-neutral-600">{m.description}</p>
              {m.legal_reference && (
                <p className="mt-1 text-[0.7rem] text-neutral-500">{m.legal_reference}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {measures.length > top.length && (
        <p className="mt-3 text-xs text-neutral-500">
          {measures.length - top.length} weitere im{" "}
          <Link to="/wiki" className="underline">
            Wiki
          </Link>
          .
        </p>
      )}
    </article>
  );
}

function RoomsOverviewGrid({ rooms }: { rooms: DashboardStatus["rooms"] }) {
  if (rooms.length === 0) {
    return (
      <article className="rounded-lg border border-[color:var(--color-border)] bg-white p-6">
        <h2 className="text-xl font-semibold">Räume</h2>
        <p className="mt-2 text-sm text-neutral-600">
          Noch keine Räume angelegt.{" "}
          <Link to="/raeume" className="underline">
            Räume verwalten
          </Link>
          .
        </p>
      </article>
    );
  }
  return (
    <article>
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Räume</h2>
        <Link to="/raeume" className="text-sm text-[color:var(--color-brand-accent)] underline">
          Alle anzeigen
        </Link>
      </header>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rooms.map((r) => {
          const lvl = r.last_entry ? LEVEL_TO_CONFIRMED[r.last_entry.confirmed_level] : null;
          return (
            <li key={r.room_id}>
              <Link
                to={`/raeume/${r.room_id}`}
                className="flex h-full flex-col gap-2 rounded-lg border border-[color:var(--color-border)] bg-white p-4 hover:border-[color:var(--color-border-strong)]"
              >
                <header className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold">{r.name}</h3>
                    {r.etage && <p className="text-xs text-neutral-500">{r.etage}</p>}
                  </div>
                  {lvl != null && <HeatBadge level={lvl} temp={r.last_entry?.indoor_temp_c} compact />}
                </header>
                {r.last_entry ? (
                  <p className="text-xs text-neutral-500">
                    Letzte Messung:{" "}
                    {new Date(r.last_entry.event_at).toLocaleString("de-DE", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-neutral-500">Noch keine Messung</p>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </article>
  );
}

function DashboardMeta({ data }: { data: DashboardStatus }) {
  return (
    <footer className="text-xs text-neutral-500">
      <p>
        Stand: {new Date(data.generated_at).toLocaleString("de-DE", { timeStyle: "short" })} ·{" "}
        Messungen letzte 7 Tage: <strong>{data.entries_last_7d}</strong>
      </p>
    </footer>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-10 animate-pulse rounded bg-[color:var(--color-surface-sunken)]" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:gap-6">
        <div className="h-56 animate-pulse rounded-2xl bg-[color:var(--color-surface-sunken)]" />
        <div className="h-56 animate-pulse rounded-2xl bg-[color:var(--color-surface-sunken)]" />
      </div>
      <div className="h-32 animate-pulse rounded-2xl bg-[color:var(--color-surface-sunken)]" />
      <div className="h-12 animate-pulse rounded-md bg-[color:var(--color-surface-sunken)]" />
      <div className="h-40 animate-pulse rounded-lg bg-[color:var(--color-surface-sunken)]" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-lg bg-[color:var(--color-surface-sunken)]"
          />
        ))}
      </div>
    </div>
  );
}
